import React, { useState, useEffect, useCallback } from 'react';

export default function ModifierGroups() {
  const [groups, setGroups]     = useState([]);
  const [allMods, setAllMods]   = useState([]);
  const [editGroup, setEditGroup] = useState(null);
  const [saving, setSaving]     = useState(false);
  const [toast, setToast]       = useState('');

  function getToken() {
    return window.__cafeApiToken || localStorage.getItem('cafepos_token');
  }

  const load = useCallback(async () => {
    const token = getToken();
    const [gRes, mRes] = await Promise.all([
      fetch('/api/modifier-groups', { headers: { Authorization: `Bearer ${token}` } }),
      fetch('/api/menu/modifiers',  { headers: { Authorization: `Bearer ${token}` } }),
    ]);
    setGroups(await gRes.json());
    setAllMods(await mRes.json());
  }, []);

  useEffect(() => { load(); }, [load]);

  function showToast(msg) { setToast(msg); setTimeout(() => setToast(''), 2500); }

  async function saveGroup(form) {
    setSaving(true);
    const token = getToken();
    try {
      let groupId = form.id;
      if (form.id) {
        await fetch(`/api/modifier-groups/${form.id}`, {
          method: 'PATCH',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name, required: form.required, sort: form.sort, multi_select: form.multi_select }),
        });
      } else {
        const res = await fetch('/api/modifier-groups', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
          body: JSON.stringify({ name: form.name, required: form.required, sort: form.sort, multi_select: form.multi_select }),
        });
        const data = await res.json();
        groupId = data.id;
      }
      await fetch(`/api/modifier-groups/${groupId}/items`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', Authorization: `Bearer ${token}` },
        body: JSON.stringify({ item_ids: form.item_ids, default_item_id: form.default_item_id }),
      });
      showToast('Zapisano grupę');
      setEditGroup(null);
      load();
    } catch (err) { showToast('Błąd: ' + err.message); }
    finally { setSaving(false); }
  }

  async function deleteGroup(id) {
    if (!window.confirm('Usunąć tę grupę modyfikatorów?')) return;
    const token = getToken();
    await fetch(`/api/modifier-groups/${id}`, { method: 'DELETE', headers: { Authorization: `Bearer ${token}` } });
    showToast('Usunięto grupę');
    load();
  }

  return (
    <div style={{ padding: 24, maxWidth: 800 }}>
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 24 }}>
        <div>
          <h2 style={{ fontSize: 18, fontWeight: 500 }}>Grupy modyfikatorów</h2>
          <p style={{ fontSize: 13, color: 'var(--text-muted)', marginTop: 4 }}>
            Grupuj modyfikatory i przypisuj grupy do pozycji menu
          </p>
        </div>
        <button className="btn-primary" style={{ width: 'auto', padding: '9px 16px' }}
          onClick={() => setEditGroup({ name: '', required: 0, sort: groups.length, item_ids: [], multi_select: 1 })}>
          + Nowa grupa
        </button>
      </div>

      {groups.length === 0 && (
        <div style={{ textAlign: 'center', padding: 40, color: 'var(--text-muted)', fontSize: 14 }}>
          Brak grup — dodaj pierwszą grupę modyfikatorów
        </div>
      )}

      {groups.map(group => (
        <div key={group.id} style={{ background: 'var(--surface)', border: '0.5px solid var(--border)', borderRadius: 10, marginBottom: 12, overflow: 'hidden' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 12, padding: '14px 16px', borderBottom: group.items.length ? '0.5px solid var(--border)' : 'none' }}>
            <div style={{ flex: 1 }}>
              <span style={{ fontSize: 15, fontWeight: 500 }}>{group.name}</span>
              {group.required ? <span style={{ marginLeft: 8, fontSize: 11, background: '#FCEBEB', color: 'var(--red)', padding: '2px 7px', borderRadius: 4 }}>wymagana</span> : null}
              <span style={{ marginLeft: 8, fontSize: 11, background: 'var(--bg)', color: 'var(--text-muted)', padding: '2px 7px', borderRadius: 4 }}>
                {group.multi_select ? 'wielokrotny wybór' : 'pojedynczy wybór'}
              </span>
            </div>
            <span style={{ fontSize: 12, color: 'var(--text-muted)' }}>{group.items.length} modyfikatorów</span>
            <div style={{ display: 'flex', gap: 6 }}>
              <button className="btn-sm" onClick={() => setEditGroup({ ...group, item_ids: group.items.map(i => i.id), default_item_id: group.items.find(i => i.is_default)?.id || null })}>Edytuj</button>
              <button className="btn-sm danger" onClick={() => deleteGroup(group.id)}>Usuń</button>
            </div>
          </div>
          {group.items.length > 0 && (
            <div style={{ display: 'flex', gap: 6, padding: '10px 16px', flexWrap: 'wrap' }}>
              {group.items.map(item => (
                <span key={item.id} style={{ fontSize: 12, background: item.is_default ? 'var(--blue-light)' : 'var(--bg)', border: '0.5px solid var(--border)', borderRadius: 4, padding: '3px 10px' }}>
                  {!!item.is_default && '★ '}{item.name} {Number(item.price) > 0 ? `+${Number(item.price).toFixed(2)} zł` : ''}
                </span>
              ))}
            </div>
          )}
        </div>
      ))}

      {editGroup && (
        <GroupModal
          group={editGroup}
          allMods={allMods}
          onSave={saveGroup}
          onClose={() => setEditGroup(null)}
          saving={saving}
        />
      )}
      {toast && <div className="toast">{toast}</div>}
    </div>
  );
}

function GroupModal({ group, allMods, onSave, onClose, saving }) {
  const [form, setForm] = useState({
    id:       group.id,
    name:     group.name || '',
    required: group.required || 0,
    sort:     group.sort || 0,
    item_ids: group.item_ids || [],
    default_item_id: group.default_item_id || null,
    multi_select: group.multi_select !== undefined ? group.multi_select : 1,
  });

  function toggleItem(id) {
    setForm(f => {
      const included = f.item_ids.includes(id);
      return {
        ...f,
        item_ids: included ? f.item_ids.filter(i => i !== id) : [...f.item_ids, id],
        // Jeśli odznaczamy modyfikator, który był domyślny - wyczyść domyślny
        default_item_id: (included && f.default_item_id === id) ? null : f.default_item_id,
      };
    });
  }

  function setDefault(id) {
    setForm(f => ({ ...f, default_item_id: f.default_item_id === id ? null : id }));
  }

  return (
    <div style={{ position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100 }}>
      <div style={{ background: 'var(--surface)', borderRadius: 12, padding: 24, width: 440, maxWidth: '95vw', maxHeight: '85vh', overflow: 'auto' }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 20 }}>
          <h3 style={{ fontSize: 16, fontWeight: 500 }}>{form.id ? 'Edytuj grupę' : 'Nowa grupa'}</h3>
          <button onClick={onClose} style={{ background: 'none', border: 'none', fontSize: 20, color: 'var(--text-muted)', cursor: 'pointer' }}>×</button>
        </div>

        <div className="field">
          <label>Nazwa grupy</label>
          <input value={form.name} onChange={e => setForm(f => ({ ...f, name: e.target.value }))} placeholder="np. Rodzaj mleka, Syropy" />
        </div>

        <div className="field">
          <label>Kolejność wyświetlania</label>
          <input type="number" min="0" value={form.sort} onChange={e => setForm(f => ({ ...f, sort: parseInt(e.target.value) || 0 }))} />
        </div>

        <label style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 13, marginBottom: 16, cursor: 'pointer' }}>
          <input type="checkbox" checked={!!form.required} onChange={e => setForm(f => ({ ...f, required: e.target.checked ? 1 : 0 }))} />
          Wybór obowiązkowy (kelner musi wybrać)
        </label>

        <div className="field">
          <label>Tryb wyboru</label>
          <div style={{ display: 'flex', gap: 8 }}>
            <button type="button" onClick={() => setForm(f => ({ ...f, multi_select: 1 }))}
              style={{
                flex: 1, padding: '9px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                border: `${form.multi_select ? '2px solid var(--blue)' : '0.5px solid var(--border)'}`,
                background: form.multi_select ? 'var(--blue-light)' : 'none',
                color: form.multi_select ? 'var(--blue-dark)' : 'var(--text-primary)',
                fontWeight: form.multi_select ? 500 : 400,
              }}>
              ☑️ Wielokrotny (checkboxy)
            </button>
            <button type="button" onClick={() => setForm(f => ({ ...f, multi_select: 0 }))}
              style={{
                flex: 1, padding: '9px 12px', borderRadius: 8, cursor: 'pointer', fontSize: 13,
                border: `${!form.multi_select ? '2px solid var(--blue)' : '0.5px solid var(--border)'}`,
                background: !form.multi_select ? 'var(--blue-light)' : 'none',
                color: !form.multi_select ? 'var(--blue-dark)' : 'var(--text-primary)',
                fontWeight: !form.multi_select ? 500 : 400,
              }}>
              🔘 Pojedynczy (jak wariant)
            </button>
          </div>
        </div>
        <div style={{ height: 20 }} />

        <div style={{ marginBottom: 20 }}>
          <div style={{ fontSize: 13, fontWeight: 500, marginBottom: 10 }}>
            Modyfikatory w tej grupie
            <span style={{ fontSize: 12, color: 'var(--text-muted)', fontWeight: 400, marginLeft: 8 }}>
              {form.item_ids.length} zaznaczonych
            </span>
          </div>
          {allMods.length === 0 && (
            <div style={{ fontSize: 13, color: 'var(--text-muted)', padding: '12px 0' }}>
              Brak modyfikatorów — dodaj pozycje z funkcją "Modyfikator" w panelu Menu
            </div>
          )}
          {allMods.map(mod => {
            const included = form.item_ids.includes(mod.id);
            return (
            <label key={mod.id} style={{
              display: 'flex', alignItems: 'center', gap: 10, padding: '9px 12px',
              border: `0.5px solid ${included ? 'var(--blue)' : 'var(--border)'}`,
              borderRadius: 7, marginBottom: 6, cursor: 'pointer',
              background: included ? 'var(--blue-light)' : 'none',
            }}>
              <input type="checkbox" checked={included} onChange={() => toggleItem(mod.id)} />
              {included && (
                <input type="radio" checked={form.default_item_id === mod.id}
                  onChange={() => setDefault(mod.id)}
                  onClick={e => e.stopPropagation()}
                  title="Ustaw jako domyślny" />
              )}
              <span style={{ flex: 1, fontSize: 13 }}>{mod.name}</span>
              <span style={{ fontSize: 13, color: 'var(--text-muted)' }}>
                {Number(mod.price) > 0 ? `+${Number(mod.price).toFixed(2)} zł` : 'bezpłatny'}
              </span>
            </label>
            );
          })}
          <div style={{ fontSize: 11, color: 'var(--text-muted)', marginTop: 4 }}>
            Zaznacz kółkiem przy wybranym modyfikatorze, żeby był domyślnie zaznaczony przy dodawaniu produktu do zamówienia.
          </div>
        </div>

        <div style={{ display: 'flex', gap: 8 }}>
          <button className="btn-outline" onClick={onClose} style={{ flex: 1 }}>Anuluj</button>
          <button className="btn-primary" style={{ flex: 2 }} onClick={() => onSave(form)}
            disabled={saving || !form.name || form.item_ids.length === 0}>
            {saving ? 'Zapisywanie...' : 'Zapisz grupę'}
          </button>
        </div>
      </div>
    </div>
  );
}
